"use client";

import { ColorModeToggle } from "@shared/components/ColorModeToggle";
import { Logo } from "@shared/components/Logo";
import { cn } from "@ui/lib";
import { SearchIcon } from "lucide-react";
import type { PropsWithChildren, ReactNode } from "react";
import { Footer } from "./Footer";

/**
 * Layout for everything before the admin: sign-in, password reset,
 * invitations. One focused column; on large screens a quiet brand panel
 * shows what the product is for.
 */
export function AuthWrapper({
	children,
	contentClass,
}: PropsWithChildren<{ contentClass?: string }>) {
	return (
		<div className="grid min-h-svh bg-background lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
			<div className="flex min-h-svh min-w-0 flex-col px-5 pt-5 pb-4 sm:px-10 sm:pt-8">
				<header>
					<Logo />
				</header>
				<main className="flex flex-1 items-center justify-center py-10">
					<div className={cn("w-full max-w-sm", contentClass)}>
						{children}
					</div>
				</main>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<Footer />
					<ColorModeToggle />
				</div>
			</div>
			<BrandPanel />
		</div>
	);
}

/** Page title for auth screens: one clear heading, one line of help. */
export function AuthHeading({
	title,
	description,
	className,
}: {
	title: ReactNode;
	description?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn("mb-8 flex flex-col gap-2", className)}>
			<h1 className="text-balance font-semibold text-2xl tracking-tight">
				{title}
			</h1>
			{description ? (
				<p className="text-pretty text-muted-foreground text-sm">
					{description}
				</p>
			) : null}
		</div>
	);
}

function BrandPanel() {
	return (
		<aside className="relative m-2 ml-0 hidden flex-col justify-between gap-10 overflow-hidden rounded-3xl bg-sidebar p-10 ring-1 ring-foreground/5 lg:flex xl:p-14 dark:ring-foreground/10">
			<div className="flex flex-1 items-center justify-center">
				<ConversationPreview />
			</div>
			<div className="flex max-w-lg flex-col gap-3">
				<p className="text-balance font-semibold text-3xl tracking-tight">
					Your shoppers ask. Your catalog answers.
				</p>
				<p className="text-pretty text-muted-foreground">
					In English, Arabic and Arabizi.
				</p>
			</div>
		</aside>
	);
}

const PREVIEW_PRODUCTS = [
	{ name: "Garnier Fructis Hair Food, 350 ml", price: "$5.90" },
	{ name: "Pantene Repair & Protect, 400 ml", price: "$6.50" },
] as const;

/** A static, illustrative exchange built from UI tokens, not an image. */
function ConversationPreview() {
	return (
		<div
			aria-hidden
			className="flex w-full max-w-md select-none flex-col gap-3 rounded-3xl bg-background p-5 text-sm shadow-(--shadow-raised) ring-1 ring-foreground/6 dark:ring-foreground/10"
		>
			<div
				dir="auto"
				className="ml-auto max-w-[80%] rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-primary-foreground"
			>
				3endkon shampoo lal sha3r el neshef?
			</div>
			<div className="flex items-center gap-2 text-muted-foreground text-xs">
				<SearchIcon className="size-3.5" />
				<span>Searched "dry hair shampoo"</span>
				<span className="rounded-full bg-muted px-2 py-0.5 tabular">
					8 products
				</span>
			</div>
			<div className="max-w-[85%] rounded-2xl rounded-bl-md bg-muted px-3.5 py-2">
				Eh akid! Hayde l tnen l aktar shi byetlabo:
			</div>
			<ul className="flex flex-col divide-y divide-border/70 rounded-2xl ring-1 ring-foreground/6 dark:ring-foreground/10">
				{PREVIEW_PRODUCTS.map((product) => (
					<li
						key={product.name}
						className="flex items-center gap-3 px-3 py-2.5"
					>
						<span className="size-8 shrink-0 rounded-lg bg-muted" />
						<span className="min-w-0 flex-1 truncate">
							{product.name}
						</span>
						<span className="font-medium tabular">
							{product.price}
						</span>
						<span className="rounded-full bg-brand/12 px-2 py-0.5 font-medium text-brand text-xs">
							Add
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
